// src/stores/v2/commodity.ts
import { defineStore } from 'pinia'
import { ref, readonly } from 'vue'
import { getCommodityService } from '@/services/serviceFactory'
import { CommoditySearch } from '@/services/commodity/CommoditySearch'
import { CommodityService } from '@/services/commodity/CommodityService'
import { useSession } from '@/stores/authStore'
import { type CommodityType } from '@/types/CommodityType'
import { useTransactionStore } from '@/stores/transactionStore'
import type { TransactionRecord } from '@/types/Transaction'
import type { TopCommodityRow } from '@/reports/topCommodities'

export const useCommodityStoreV2 = defineStore('commodity.v2', () => {
  // State
  const loading = ref(false)
  const cache = ref({} as Record<string, CommodityType>)
  const topCommoditiesCache = ref<{
    data: TopCommodityRow[]
    transactionType: 'purchased' | 'sold' | 'combined'
    lastUpdated: number
  } | null>(null)

  // Cache TTL: 5 minutes
  const CACHE_TTL_MS = 5 * 60 * 1000

  // Service instance - we'll get it lazily
  let _service: CommodityService | null = null

  async function getService(): Promise<CommodityService> {
    if (!_service) {
      _service = await getCommodityService()
    }
    return _service
  }

  // Getters
  const getSearch = async () => {
    // Get session from authStore
    const session = useSession()

    // Wait for session to be ready
    let attempts = 0
    while (!session.tenantId && attempts < 50) {
      // Wait up to 5 seconds
      await new Promise((resolve) => setTimeout(resolve, 100))
      attempts++
    }

    if (!session.tenantId) {
      throw new Error('Session not ready: tenantId is required')
    }

    const service = await getService()
    return new CommoditySearch(session, service['firestore'])
  }

  // Actions
  async function get(id: string): Promise<CommodityType | null> {
    // Check cache first
    if (cache.value[id]) return cache.value[id]

    // Fetch from Firestore using service
    const service = await getService()
    const commodity = await service.get(id)
    if (commodity) cache.value[id] = commodity
    return commodity
  }

  async function getActiveCommodityTypes(): Promise<CommodityType[]> {
    const service = await getService()
    return await service.getActiveCommodityTypes()
  }

  async function create(data: Partial<CommodityType>): Promise<string> {
    loading.value = true
    try {
      const service = await getService()
      return await service.create(data)
    } finally {
      loading.value = false
    }
  }

  async function update(id: string, data: Partial<CommodityType>): Promise<void> {
    loading.value = true
    try {
      const service = await getService()
      await service.update(id, data)
      // Clear cache for this item
      delete cache.value[id]
      // Invalidate topCommodities cache since commodity data changed
      invalidateTopCommoditiesCache()
    } finally {
      loading.value = false
    }
  }

  async function remove(id: string): Promise<void> {
    loading.value = true
    try {
      const service = await getService()
      await service.delete(id)
      // Clear cache for this item
      delete cache.value[id]
      // Invalidate topCommodities cache since commodity was deleted
      invalidateTopCommoditiesCache()
    } finally {
      loading.value = false
    }
  }

  function clearCache() {
    cache.value = {}
    invalidateTopCommoditiesCache()
  }

  function invalidateTopCommoditiesCache() {
    topCommoditiesCache.value = null
  }

  /**
   * Get top commodities with caching.
   * Cache is invalidated when:
   * - Cache is older than TTL
   * - Transaction type changes
   * - Transactions are updated (via watch on transactionStore)
   */
  async function getTopCommodities(
    transactionType: 'purchased' | 'sold' | 'combined' = 'purchased',
  ): Promise<TopCommodityRow[]> {
    console.log('🔍 Getting top commodities', transactionType)
    const now = Date.now()

    // Check if cache is valid
    if (
      topCommoditiesCache.value &&
      topCommoditiesCache.value.transactionType === transactionType &&
      now - topCommoditiesCache.value.lastUpdated < CACHE_TTL_MS
    ) {
      const age = Math.round((now - topCommoditiesCache.value.lastUpdated) / 1000)
      console.log(
        `✅ Cache HIT for topCommodities (${transactionType}) - ${topCommoditiesCache.value.data.length} items, ${age}s old`,
      )
      return topCommoditiesCache.value.data
    }

    console.log(
      `❌ Cache MISS for topCommodities (${transactionType}) - ${
        !topCommoditiesCache.value
          ? 'no cache'
          : topCommoditiesCache.value.transactionType !== transactionType
            ? 'different transaction type'
            : 'cache expired'
      }`,
    )

    // Calculate top commodities
    loading.value = true
    try {
      const transactionStore = useTransactionStore()
      await transactionStore.load()

      // Get transactions with items, filtered by transaction type
      let transactions: TransactionRecord[] = transactionStore.items.filter(
        (t) => !t.is_deleted && t.items && t.items.length > 0,
      )

      // Apply transaction type filter
      if (transactionType === 'purchased') {
        transactions = transactions.filter((t) => t.transaction_type === 'purchase')
      } else if (transactionType === 'sold') {
        transactions = transactions.filter((t) => t.transaction_type === 'sale')
      }
      // For 'combined', we use all transactions (no additional filtering)

      // Load commodity types for additional information
      const commodityTypes = await getActiveCommodityTypes()
      const commodityMap = new Map(commodityTypes.map((c) => [c.id, c]))

      // Aggregate data by commodity
      const commodityTotals = new Map<
        string,
        {
          commodityName: string
          commodityId: string
          category: string
          totalValue: number
          totalWeight: number
          transactionCount: number
          totalPricePerLb: number
          pricePerLbCount: number
        }
      >()

      for (const tx of transactions) {
        for (const item of tx.items) {
          const commodityId = item.commodity_type_id

          // Get commodity info from loaded commodity types
          const commodityType = commodityMap.get(commodityId)
          const commodityName = commodityType?.name || item.commodity_name || 'Unknown'
          const category = commodityType?.category || 'Unknown'

          const current = commodityTotals.get(commodityId) || {
            commodityName,
            commodityId,
            category,
            totalValue: 0,
            totalWeight: 0,
            transactionCount: 0,
            totalPricePerLb: 0,
            pricePerLbCount: 0,
          }

          current.totalValue += Number(item.total_price || 0)
          current.totalWeight += Number(item.weight?.net || 0)
          current.transactionCount += 1

          // Track price per lb for average calculation
          if (item.price_per_lb && item.price_per_lb > 0) {
            current.totalPricePerLb += Number(item.price_per_lb)
            current.pricePerLbCount += 1
          }

          commodityTotals.set(commodityId, current)
        }
      }

      // Convert to rows and calculate averages
      const rows: TopCommodityRow[] = Array.from(commodityTotals.values())
        .map((data) => ({
          commodityName: data.commodityName,
          commodityId: data.commodityId,
          category: data.category,
          totalValue: data.totalValue,
          totalWeight: data.totalWeight,
          transactionCount: data.transactionCount,
          averagePricePerLb:
            data.pricePerLbCount > 0 ? data.totalPricePerLb / data.pricePerLbCount : 0,
        }))
        .sort((a, b) => b.totalValue - a.totalValue) // Sort by total value descending

      // Update cache
      topCommoditiesCache.value = {
        data: rows,
        transactionType,
        lastUpdated: now,
      }

      console.log(`💾 Cache UPDATED for topCommodities (${transactionType}) - ${rows.length} items`)

      return rows
    } finally {
      loading.value = false
    }
  }

  return {
    // State
    loading: readonly(loading),
    cache: readonly(cache),

    // Getters
    search: getSearch,

    // Actions
    get,
    getActiveCommodityTypes,
    getTopCommodities,
    create,
    update,
    remove,
    clearCache,
    invalidateTopCommoditiesCache,
  }
})
